import {TemplateVariable} from 'components/customer/Preview3D/usePreview3DData';
import {
    CabinetMesh,
    RoomPlannerProductWithTemplate,
} from 'components/customer/RoomPlanner/types';

interface AssignBasicUserDataParams {
    cabinetGroup: CabinetMesh;
    product: RoomPlannerProductWithTemplate;
    productValues: RoomPlannerProductWithTemplate;
    variables: TemplateVariable;
    wallHeight: number;
    width: number;
    toeKickHeight: number;
    isPanelProduct: boolean;
    isPanelProductWithWidth: boolean;
    isPanelProductWithLength: boolean;
    isFloorProduct: boolean;
    isLShapedProduct: boolean;
    isUpperProduct: boolean;
    isBaseProduct: boolean;
    isTallProduct: boolean;
    cabinetStyle: number;
    isLShapePanel: boolean;
    isManualPanelProduct: boolean;
    sitOnFloor: boolean;
    tempMesh: boolean;
    isFillerProduct: boolean;
    isVerminProduct: boolean;
    isBulkheadComponent: boolean;
    isTallLShape: boolean;
    innerDrawerOnly: boolean;
    isKeyShapePanel: boolean;
    isUShapedPanel: boolean;
    hasUShapedShelves: boolean;
    isKickerLengthProduct: boolean;
    productsWithWidthOffset: boolean;
}

/**
 * Assigns basic user data properties to a cabinet group
 */
export const assignBasicUserData = ({
    cabinetGroup,
    product,
    productValues,
    variables,
    wallHeight,
    width,
    toeKickHeight,
    isPanelProduct,
    isPanelProductWithWidth,
    isPanelProductWithLength,
    isFloorProduct,
    isLShapedProduct,
    isUpperProduct,
    isBaseProduct,
    isTallProduct,
    cabinetStyle,
    isLShapePanel,
    isManualPanelProduct,
    sitOnFloor,
    tempMesh,
    isFillerProduct,
    isVerminProduct,
    isBulkheadComponent,
    isTallLShape,
    innerDrawerOnly,
    isKeyShapePanel,
    isUShapedPanel,
    hasUShapedShelves,
    isKickerLengthProduct,
    productsWithWidthOffset,
}: AssignBasicUserDataParams): void => {
    cabinetGroup.userData.id = product.duplicate_id
        ? product.duplicate_id
        : product.job_cabinet_id;

    cabinetGroup.userData.values = productValues;
    cabinetGroup.userData.templateName =
        product.template_3d[0].attributes.template;
    cabinetGroup.userData.onHide = () => {
        cabinetGroup.visible = false;
    };
    cabinetGroup.userData.wallHeight = wallHeight;

    // Cabinet properties
    cabinetGroup.userData.ignoreSnapping = !!variables.innerDrawerOnly;
    cabinetGroup.userData.isPanelProduct = isPanelProduct;
    cabinetGroup.userData.isPanelProductWithWidth = isPanelProductWithWidth;
    cabinetGroup.userData.isPanelProductWithLength = isPanelProductWithLength;
    cabinetGroup.userData.isFloorProduct = isFloorProduct;
    cabinetGroup.userData.isLShapedProduct = isLShapedProduct;
    cabinetGroup.userData.isUpperProduct = isUpperProduct;
    cabinetGroup.userData.isBaseProduct = isBaseProduct;
    cabinetGroup.userData.isTallProduct = isTallProduct;
    cabinetGroup.userData.cabinetStyle = cabinetStyle;
    cabinetGroup.userData.isLShapePanel = isLShapePanel;
    cabinetGroup.userData.isManualPanel = isManualPanelProduct;
    cabinetGroup.userData.currentDirection = 'front'; // Default facing direction
    cabinetGroup.userData.sitOnFloor = sitOnFloor;
    cabinetGroup.userData.isTempMesh = tempMesh;
    cabinetGroup.userData.widthOffset = 0;
    cabinetGroup.userData.isFillerProduct = isFillerProduct;
    cabinetGroup.userData.toeKickHeight = toeKickHeight;
    cabinetGroup.userData.isVerminProduct = isVerminProduct;
    cabinetGroup.userData.isRightReturnProduct = Boolean(
        !variables.isLeftReturn && variables.isReturnProduct
    );
    cabinetGroup.userData.isBulkheadComponent = isBulkheadComponent;
    cabinetGroup.userData.isTallLShape = isTallLShape;
    cabinetGroup.userData.hasInnerDrawer = variables.innerDrawerCount > 0;
    cabinetGroup.userData.innerDrawerOnly = innerDrawerOnly;
    cabinetGroup.userData.noInteractions = variables.noInteractions;
    cabinetGroup.userData.ignoreOverlap =
        isFloorProduct || !!variables.innerDrawerOnly;
    cabinetGroup.userData.isReturnProduct = variables.isReturnProduct;
    cabinetGroup.userData.isNonManualPanel = Boolean(
        variables.isNonManualPanel
    );
    cabinetGroup.userData.isKickerLengthProduct = isKickerLengthProduct;
    cabinetGroup.userData.isKeyShapePanel = isKeyShapePanel;
    cabinetGroup.userData.isUShapedPanel = isUShapedPanel;
    cabinetGroup.userData.hasUShapedShelves = hasUShapedShelves;

    if (productsWithWidthOffset) {
        cabinetGroup.userData.widthOffset = !width ? 3.5 : width * 0.2;
    }

    if (isFloorProduct) {
        cabinetGroup.userData.widthOffset = Boolean(product?.cabinet_edge_W1)
            ? Number(product?.cabinet_ext_thickness) * 0.2
            : 0;

        cabinetGroup.userData.kickerFrameEdge = {
            front: Boolean(product?.cabinet_edge_L2),
            back: Boolean(product?.cabinet_edge_L1),
            left: Boolean(product?.cabinet_edge_W1),
            right: Boolean(product?.cabinet_edge_W2),
        };
    }
};
